import { Fragment, useMemo, type ReactNode } from 'react';
import TermsAndConditionsRenderAlternatingText from '../AlternatingText';
import type {
  PdfBulletListType,
  PdfComponentBulletListContent,
  PdfComponentBulletListI,
} from '../typed';

const getAlpha = (index: number, capital: boolean): string => {
  const offset = capital ? 65 : 97;
  if (index < 26) {
    return String.fromCharCode(offset + index);
  }
  if (index > 27 * 26 - 1) {
    return '-';
  }
  const perfixLetterInt = Math.floor(index / 26);
  const letterInt = index % 26;
  return `${String.fromCharCode(offset + perfixLetterInt)}${String.fromCharCode(
    offset + letterInt,
  )}`;
};

const romanize = (num: number): string => {
  if (num < 1 || num > 3999) return '-';
  const numerals = [
    ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'], // 1-9
    ['X', 'XX', 'XXX', 'XL', 'L', 'LX', 'LXX', 'LXXX', 'XC'], // 10-90
    ['C', 'CC', 'CCC', 'CD', 'D', 'DC', 'DCC', 'DCCC', 'CM'], // 100-900
    ['M', 'MM', 'MMM'], // 1000-3000
  ];
  const digits = Math.round(num).toString().split('');
  return digits.reduce((roman, digit, index) => {
    if (digit === '0') return roman;
    const position = digits.length - 1 - index;
    const current = numerals[position][parseInt(digit, 10) - 1].toLowerCase();
    const appended = `${roman}${current}`;
    return appended;
  }, '');
};

const getBulletType = (
  bulletType: PdfBulletListType,
  index: number,
  prefix?: string,
): ReactNode => {
  if (bulletType === 'blank') return <div className="h-[20px] w-[6px] flex" />;
  if (bulletType === 'dot')
    return (
      <div className="h-[20px] flex">
        <div className="my-auto w-1.5 h-1.5 rounded-full bg-neutral-900" />
      </div>
    );
  if (bulletType === 'disc')
    return (
      <div className="h-[20px] flex">
        <div className="my-auto w-[5px] h-[5px] rounded-full bg-neutral-000 border border-neutral-900" />
      </div>
    );
  if (bulletType === 'dash')
    return (
      <div className="h-[20px] flex">
        <div className="my-auto w-1 h-[1px] border border-neutral-900" />
      </div>
    );
  if (bulletType === 'number')
    return (
      <span className="text-12-18 md:text-14-22">
        ({prefix ? `${prefix}.` : ''}
        {index + 1})
      </span>
    );
  if (bulletType === 'roman')
    return (
      <span className="text-12-18 md:text-14-22">({romanize(index + 1)})</span>
    );
  const alpha = getAlpha(index, bulletType === 'capitalAlpha');
  return (
    <span className="text-12-18 md:text-14-22">
      ({prefix ? `${prefix}.` : ''}
      {alpha})
    </span>
  );
};

const getMainContent = (content: PdfComponentBulletListContent): ReactNode => {
  if (typeof content.copy === 'string') {
    return <span className="text-12-18 md:text-14-22">{content.copy}</span>;
  }
  return <TermsAndConditionsRenderAlternatingText {...content.copy} />;
};

const getSubContent = (
  content: PdfComponentBulletListContent,
): ReactNode | null => {
  if (!content.subcopy) return null;
  if (typeof content.subcopy === 'string') {
    return <span className="text-12-18 md:text-14-22">{content.subcopy}</span>;
  }
  return <TermsAndConditionsRenderAlternatingText {...content.subcopy} />;
};

const TermsAndConditionsRenderBulletList: React.FunctionComponent<PdfComponentBulletListI> =
  function ({ bulletType, content, prefix }) {
    const sections: ReactNode[] = useMemo(
      () =>
        content.map((subcontent, index) => {
          const key = `${index}`;
          const bullet = getBulletType(bulletType, index, prefix);
          const mainContent = getMainContent(subcontent);
          const subContent = getSubContent(subcontent);
          return (
            <Fragment key={key}>
              <div className="flex mb-auto">{bullet}</div>
              <div className="flex flex-col gap-y-1 md:gap-y-2">
                {mainContent}
                {subcontent.sublist && (
                  <TermsAndConditionsRenderBulletList
                    bulletType={subcontent.sublist.bulletType}
                    content={subcontent.sublist.content}
                  />
                )}
                {subContent}
              </div>
            </Fragment>
          );
        }),
      [bulletType, content, prefix],
    );
    return (
      <div className="grid grid-cols-[min-content_auto] gap-1 md:gap-3">
        {sections}
      </div>
    );
  };

export default TermsAndConditionsRenderBulletList;
